/**
 * Send Email Modal
 *
 * Modal for composing and sending emails from record detail pages.
 * Loads templates from useEmailTemplates (Gobodo when enabled); sends via useSendEmail
 * with template + context so placeholders are resolved from context.
 * Creates an Activity record when email is sent.
 */

import React, { useState, useEffect } from 'react';
import { X, Send, FileText, ChevronDown } from 'lucide-react';
import { activityStorage } from '../../services/activityStorage';
import { RecordType } from '../../types/activity';
import { useToast } from '../../contexts/ToastContext';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useEmailTemplates } from '../../services/queries/emailTemplateQueries';
import { useSendEmail } from '../../hooks/useSendEmail';
import type { EmailTemplateContext } from '../../utils/emailTemplateUtils';
import type { User } from '../../types/user';

interface SendEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  recordId: string;
  recordType: RecordType;
  recipientEmail?: string;
  recipientName?: string;
  onEmailSent?: () => void;
  /** Optional context for merge fields (e.g. case + user on case detail). User is merged from useCurrentUser when not provided. */
  emailContext?: EmailTemplateContext;
  /** Filter templates by department (e.g. 'cybersweep' for CyberSweep pages) */
  department?: 'all' | 'credit_management' | 'cybersweep' | 'credit_block';
}

interface TemplateOption {
  id: string;
  name: string;
  subject: string;
  body: string;
}

export const SendEmailModal: React.FC<SendEmailModalProps> = ({
  isOpen,
  onClose,
  recordId,
  recordType,
  recipientEmail = '',
  recipientName = '',
  onEmailSent,
  emailContext,
  department = 'all',
}) => {
  const { success, error: showError } = useToast();
  const { userId, userName, email: userEmail, firstName, lastName } = useCurrentUser();
  const { data: templateList = [] } = useEmailTemplates({ department });
  const sendEmailMutation = useSendEmail();
  const [showTemplates, setShowTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateOption | null>(null);
  const [emailData, setEmailData] = useState({
    to: recipientEmail,
    cc: '',
    bcc: '',
    subject: '',
    body: '',
  });

  const templateOptions: TemplateOption[] = templateList.map((t) => ({
    id: t.id,
    name: t.name,
    subject: t.subject,
    body: t.body,
  }));

  const defaultUser: User = {
    id: userId,
    email: userEmail,
    firstName: firstName ?? '',
    lastName: lastName ?? '',
    role: 'team_user',
    department: 'credit_management',
    permissions: [],
    isActive: true,
    createdDate: '',
    createdBy: '',
  };
  const mergedContext: EmailTemplateContext = {
    ...emailContext,
    user: emailContext?.user ?? defaultUser,
  };

  useEffect(() => {
    if (isOpen) {
      setEmailData({
        to: recipientEmail,
        cc: '',
        bcc: '',
        subject: '',
        body: '',
      });
      setSelectedTemplate(null);
      setShowTemplates(false);
    }
  }, [isOpen, recipientEmail]);

  const handleTemplateSelect = (template: TemplateOption) => {
    setEmailData((prev) => ({ ...prev, subject: template.subject, body: template.body }));
    setSelectedTemplate(template);
    setShowTemplates(false);
  };

  const handleSend = async () => {
    if (!emailData.to?.trim() || !emailData.subject?.trim()) return;

    const toAddresses = emailData.to.trim().split(/[\s,]+/).filter(Boolean);
    if (!toAddresses.length) return;

    try {
      if (selectedTemplate) {
        await sendEmailMutation.mutateAsync({
          to: toAddresses,
          cc: emailData.cc?.trim() ? emailData.cc.trim().split(/[\s,]+/).filter(Boolean) : undefined,
          bcc: emailData.bcc?.trim() ? emailData.bcc.trim().split(/[\s,]+/).filter(Boolean) : undefined,
          composeFromTemplate: {
            template: {
              subject: selectedTemplate.subject,
              bodyHtml: selectedTemplate.body,
            },
            context: mergedContext,
          },
        });
      } else {
        await sendEmailMutation.mutateAsync({
          to: toAddresses,
          cc: emailData.cc?.trim() ? emailData.cc.trim().split(/[\s,]+/).filter(Boolean) : undefined,
          bcc: emailData.bcc?.trim() ? emailData.bcc.trim().split(/[\s,]+/).filter(Boolean) : undefined,
          subject: emailData.subject,
          body: emailData.body,
        });
      }

      await activityStorage.create({
        type: 'email',
        recordId,
        recordType,
        userId,
        userName,
        subject: emailData.subject,
        description: emailData.body.substring(0, 500),
        recipientEmail: emailData.to,
        recipientName: recipientName || undefined,
        templateId: selectedTemplate?.id,
        templateName: selectedTemplate?.name,
        direction: 'outbound',
        source: 'email_system',
      });

      success('Email sent', `Email sent to ${recipientName || emailData.to}`);
      onEmailSent?.();
      onClose();
    } catch (err) {
      console.error('[SendEmailModal] Error sending email:', err);
      showError('Send failed', err instanceof Error ? err.message : 'Failed to send email');
    }
  };

  const sending = sendEmailMutation.isPending;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Send Email
              {recipientName && (
                <span className="text-gray-500 font-normal"> to {recipientName}</span>
              )}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            {/* Template Selector */}
            <div className="relative">
              <button
                onClick={() => setShowTemplates(!showTemplates)}
                className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-gray-900 dark:text-white"
              >
                <FileText className="w-4 h-4 text-gray-500" />
                {selectedTemplate ? selectedTemplate.name : 'Select Template'}
                <ChevronDown className="w-4 h-4 text-gray-400" />
              </button>

              {showTemplates && (
                <div className="absolute top-full left-0 mt-1 w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10">
                  {templateOptions.length === 0 ? (
                    <div className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">
                      No templates
                    </div>
                  ) : (
                    templateOptions.map((template) => (
                      <button
                        key={template.id}
                        onClick={() => handleTemplateSelect(template)}
                        className="w-full px-4 py-2 text-left text-sm text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 first:rounded-t-lg last:rounded-b-lg"
                      >
                        {template.name}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* To Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                To
              </label>
              <input
                type="email"
                value={emailData.to}
                onChange={(e) => setEmailData({ ...emailData, to: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="recipient@example.com"
              />
            </div>

            {/* CC/BCC Fields (collapsed by default) */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  CC
                </label>
                <input
                  type="email"
                  value={emailData.cc}
                  onChange={(e) => setEmailData({ ...emailData, cc: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="cc@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  BCC
                </label>
                <input
                  type="email"
                  value={emailData.bcc}
                  onChange={(e) => setEmailData({ ...emailData, bcc: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="bcc@example.com"
                />
              </div>
            </div>

            {/* Subject */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Subject
              </label>
              <input
                type="text"
                value={emailData.subject}
                onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Email subject"
              />
            </div>

            {/* Body */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Message
              </label>
              <textarea
                value={emailData.body}
                onChange={(e) => setEmailData({ ...emailData, body: e.target.value })}
                rows={8}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                placeholder="Write your message..."
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSend}
              disabled={!emailData.to || !emailData.subject || sending}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Send className="w-4 h-4" />
              {sending ? 'Sending...' : 'Send Email'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
